import React, {
  createContext,
  useState,
  ReactNode,
  useEffect,
  useContext
} from 'react';
import { useNavigate } from 'react-router-dom';
//models
import {
  DocumentContextModel,
  sentenceItem,
  IControl,
  IDomain,
  documentTableItem,
  phraseItem,
  fileUploadItem,
  kbSentenceItem
} from '../models/interfaces';
// dummy data for dev
import axios from 'axios';
import { OptionDefinition } from '@cloudscape-design/components/internal/components/option/interfaces';
import { sortAndRemoveHidden } from '../helpers/sortAndDisplay';
import { TableProps } from '@cloudscape-design/components';
import { UserContext } from './authContext';
import { getKMFilters } from '../helpers/getKnowledgeMapFilters';
interface Props {
  children?: ReactNode;
}

export const DocumentContext = createContext({} as DocumentContextModel);

export const DocumentContextProvider: React.FC<Props> = (props) => {
  const { auth, token, user } = useContext(UserContext);
  const [sentences, setSentences] = useState<sentenceItem[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [domains, setDomains] = useState<IDomain[]>([]);
  const [controls, setControls] = useState<IControl[]>([]);
  const [selectedDomainOption, setSelectedDomainOption] =
    useState<OptionDefinition>({
      label: 'Option 1',
      value: '1'
    });
  const [selectedControlOptions, setSelectedControlOptions] =
    useState<OptionDefinition>({ label: 'No Selection', value: 'NS' });
  const [currentDoc, setCurrentDoc] = useState<documentTableItem>(
    {} as documentTableItem
  );
  const [currentDocId, setCurrentDocId] = useState<string | null>();
  const [selectType, setSelectType] =
    useState<TableProps.SelectionType>('single');
  const [selectedItems, setSelectedItems] = useState<
    sentenceItem[] | phraseItem[]
  >([]);
  const [notesValue, setNotesValue] = useState<string>('');
  const [filesToUpload, setFilesToUpload] = useState<fileUploadItem[]>([]);
  const [uploadProcessing, setUploadProcessing] = useState<boolean>(false);
  const [currentFolder, setCurrentFolder] = useState<string>('');
  const [knowledgeBaseModal, setKnowledgeBaseModal] = useState<boolean>(false);
  const [knowledgeBaseSentences, setKnowledgeBaseSentences] = useState<
    kbSentenceItem[]
  >([]);
  const [selectedItemsKbResults, setSelectedItemsKbResults] = useState<
    kbSentenceItem[]
  >([]);
  const [kbLoading, setKbLoading] = useState<boolean>(false);
  const [refreshKbLoading, setRefreshKbLoading] = useState<boolean>(false);
  const [updateSentenceLoading, setUpdateSentenceLoading] =
    useState<boolean>(false);
  const [freeformSelectedItems, setFreeformeSelectedItems] = useState<
    kbSentenceItem[]
  >([]);
  const [localSelectedItems, setLocalSelectedItems] = useState<
    kbSentenceItem[]
  >([]);
  const [kbFilterOptions, setKBFilterOptions] = useState<OptionDefinition[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentDocId) {
      getSentences(currentDocId);
      getDocument(currentDocId);
      // getKnowledgeBase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDocId]);

  const getDomains = async () => {
    try {
      const domResult = await axios.get(
        `${process.env.REACT_APP_API}/domains`,
        auth
      );

      setDomains(domResult.data);
    } catch (e) {
      console.log('error from get domains', e);
    }
  };

  const getControls = async () => {
    try {
      const conResult = await axios.get(
        `${process.env.REACT_APP_API}/controls`,
        auth
      );
      setControls(conResult.data);
    } catch (e) {
      console.log('error from get controls', e);
    }
  };
  const getDocument = async (documentId: string) => {
    try {
      const result = await axios.get(
        `${process.env.REACT_APP_API}/document/${currentDocId}`,
        auth
      );
      setCurrentDoc(result.data[0]);
    } catch (e) {
      console.log(e);
    }
  };

  const getSentences = async (documentId: string) => {
    const senResult = await axios.get(
      `${process.env.REACT_APP_API}/sentences/${documentId}`,
      auth
    );
    const sortedResults = sortAndRemoveHidden(senResult.data);

    setSentences([...sortedResults]);
  };

  const createPhrase = async (sentencesForPhrase: sentenceItem[]) => {
    const phraseBody = {
      sentencesForPhrase
    };
    try {
      const phraseResult = await axios.post(
        `${process.env.REACT_APP_API}/phrase`,
        phraseBody,
        auth
      );

      if (phraseResult.status === 200) {
        const tempSentences = sentences;
        for (const sentence of sentencesForPhrase) {
          const index = tempSentences.findIndex(
            (ele) => sentence._id === ele._id
          );
          tempSentences[index].hidden = true;
        }
        tempSentences.push(phraseResult.data);
        const sorted = sortAndRemoveHidden(tempSentences);
        setSentences([...sorted]);
        setSelectedItems([phraseResult.data]);
        setSelectType('single');
      }
    } catch (e) {
      console.log('error from phrase', e);
    }
  };
  useEffect(() => {
    if (selectedItems.length === 1) {
      setNotesValue(selectedItems[0].notes);
    } else {
      setNotesValue('');
    }
  }, [selectedItems]);

  const updateSentence = async () => {
    if (selectedItems.length === 1) {
      setUpdateSentenceLoading(true);
      const itemToUpdate = selectedItems[0];

      const updateBody = {
        updates: {
          notes: notesValue,
          predicted_label_domain: selectedDomainOption.label,
          predicted_label_control: selectedControlOptions.label,
          changedControls:
            selectedControlOptions.label !==
            itemToUpdate.predicted_label_control,
          changedDomains:
            selectedDomainOption.label !== itemToUpdate.predicted_label_control
        },
        sentenceId: itemToUpdate._id
      };
      let updatedItem: sentenceItem | phraseItem;
      const tempSentences = sentences;
      try {
        if ('sentences' in itemToUpdate) {
          const result = await axios.put(
            `${process.env.REACT_APP_API}/updatePhrase`,
            updateBody,
            auth
          );
          updatedItem = result.data;
        } else {
          const result = await axios.put(
            `${process.env.REACT_APP_API}/updateSentence`,
            updateBody,
            auth
          );
          updatedItem = result.data;
        }
        const indexOfUpdateItem = tempSentences.findIndex(
          (ele: sentenceItem | phraseItem) => updatedItem._id === ele._id
        );
        if (indexOfUpdateItem !== -1) {
          tempSentences[indexOfUpdateItem] = updatedItem;
          setSentences([...tempSentences]);
          setSelectedItems([updatedItem]);
        }
        setUpdateSentenceLoading(false);
      } catch (e) {
        console.log('error from update', e);
      }
    }
  };

  const breakPhraseApart = async () => {
    const tempItem = selectedItems[0];
    console.log('break phrase a[art running', tempItem);
    try {
      const result = await axios.post(
        `${process.env.REACT_APP_API}/breakphraseapart`,
        selectedItems[0],
        auth
      );
      console.log('results break apart', result);
      if (result.status === 200) {
        const newSentenceArray = sentences.filter(
          (ele) => ele._id !== tempItem._id
        );
        newSentenceArray.push(...result.data);
        const sortedArray = sortAndRemoveHidden(newSentenceArray);
        setSentences(sortedArray);
        setSelectedItems([result.data[0]]);
      }
    } catch (e) {
      console.log('error in breakapart', e);
    }
  };
  const addKBsentences = async (sentenceIds: string[], close: boolean) => {
    if (selectedItems.length === 1) {
      setUpdateSentenceLoading(true);
      const itemToUpdate = selectedItems[0];
      console.log('tem to update', itemToUpdate);
      const updateBody = {
        updates: {
          kbSentences: sentenceIds
        },
        sentenceId: itemToUpdate._id
      };
      let updatedItem: sentenceItem | phraseItem;

      try {
        if ('sentences' in itemToUpdate) {
          const result = await axios.put(
            `${process.env.REACT_APP_API}/updatePhrase`,
            updateBody,
            auth
          );
          updatedItem = result.data;
        } else {
          const result = await axios.put(
            `${process.env.REACT_APP_API}/updateSentence`,
            updateBody,
            auth
          );
          updatedItem = result.data;
        }
        console.log('UpdatedItem', updatedItem);
        if (updatedItem.documentId) {
          console.log('Updatig sentences');
          await getSentences(updatedItem.documentId);
          setKnowledgeBaseModal(false);
          if (close) {
            setShowModal(false);
          }
        }
        setUpdateSentenceLoading(false);
      } catch (e) {
        console.log('error from update', e);
      }
    }
  };
  const updateFocus = async () => {
    if (selectedItems.length === 1) {
      const itemToUpdate = selectedItems[0];
      const newLanguage =
        selectedItems[0].predicted_label_language === 'ComplianceLanguage'
          ? 'GeneralLanguage'
          : 'ComplianceLanguage';
      const updateBody = {
        updates: {
          predicted_label_language: newLanguage,
          changedLanguage: true
        },
        sentenceId: itemToUpdate._id
      };
      let updatedItem: sentenceItem | phraseItem;
      const tempSentences = sentences;
      try {
        if ('sentences' in itemToUpdate) {
          const result = await axios.put(
            `${process.env.REACT_APP_API}/updatePhrase`,
            updateBody,
            auth
          );
          updatedItem = result.data;
        } else {
          const result = await axios.put(
            `${process.env.REACT_APP_API}/updateSentence`,
            updateBody,
            auth
          );
          updatedItem = result.data;
        }
        const indexOfUpdateItem = tempSentences.findIndex(
          (ele: sentenceItem | phraseItem) => updatedItem._id === ele._id
        );
        if (indexOfUpdateItem !== -1) {
          tempSentences[indexOfUpdateItem] = updatedItem;
          setSentences([...tempSentences]);
          setSelectedItems([updatedItem]);
        }
      } catch (e) {
        console.log('error from update', e);
      }
    }
  };

  const updateRedline = async () => {
    if (selectedItems.length === 1) {
      const itemToUpdate = selectedItems[0];
      const newRedline = !itemToUpdate.redlined;
      const date = new Date();
      itemToUpdate.redlineLog.push({
        user: user.username,
        action: newRedline,
        date: date
      });
      const updateBody = {
        updates: {
          redlined: newRedline,
          redlineLog: itemToUpdate.redlineLog
        },
        sentenceId: itemToUpdate._id
      };
      let updatedItem: sentenceItem | phraseItem;
      const tempSentences = sentences;
      try {
        if ('sentences' in itemToUpdate) {
          const result = await axios.put(
            `${process.env.REACT_APP_API}/updatePhrase`,
            updateBody,
            auth
          );
          updatedItem = result.data;
        } else {
          const result = await axios.put(
            `${process.env.REACT_APP_API}/updateSentence`,
            updateBody,
            auth
          );
          updatedItem = result.data;
        }
        const indexOfUpdateItem = tempSentences.findIndex(
          (ele: sentenceItem | phraseItem) => updatedItem._id === ele._id
        );
        if (indexOfUpdateItem !== -1) {
          tempSentences[indexOfUpdateItem] = updatedItem;
          setSentences([...tempSentences]);
          setSelectedItems([updatedItem]);
        }
      } catch (e) {
        console.log('error from update', e);
      }
    }
  };

  useEffect(() => {
    if (!token) {
      return;
    }
    getControls();
    getDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const getCurrentSentenceKB = async () => {
    const tempSentence = selectedItems[0];
    if (tempSentence) {
      setKbLoading(true);
      try {
        const result = await axios.post(
          `${process.env.REACT_APP_API}/kbSearchNew`,
          {
            sentence: tempSentence
          },
          auth
        );
        console.log('results from kb search', result.data);
        setSelectedItemsKbResults(result.data);
        setKbLoading(false);
      } catch (e) {
        console.log('error from getKBresults', e);
        setSelectedItemsKbResults([]);
        setKbLoading(false);
      }
    }
  };

  useEffect(() => {
    const filtersValue = getKMFilters(selectedItemsKbResults);
    const tempArrayOfFilters: OptionDefinition[] = [];
    for (const value of filtersValue) {
      tempArrayOfFilters.push({ label: value, value: value });
    }
    setKBFilterOptions(tempArrayOfFilters);
  }, [selectedItemsKbResults]);

  const freeHandleAnalyze = async (sentenceText: string) => {
    try {
      const result = await axios.post(
        `${process.env.REACT_APP_API}/passthroughAnalysis`,
        {
          sentence: sentenceText
        },
        auth
      );

      console.log('results from passthrough search', result.data);
      return result.data;
    } catch (e) {
      console.log('error from passthrough', e);
    }
  };

  const refreshCurrentSentenceKB = async () => {
    const tempSentence = selectedItems[0];
    if (tempSentence) {
      setRefreshKbLoading(true);
      try {
        const result = await axios.post(
          `${process.env.REACT_APP_API}/kbSearchNew`,
          {
            sentence: tempSentence
          },
          auth
        );
        console.log('results from kb search', result.data);
        setRefreshKbLoading(false);
        if (currentDocId) {
          getSentences(currentDocId);
        }
        return result.data;
      } catch (e) {
        console.log('error from getKBresults', e);
        setRefreshKbLoading(false);
        return [];
      }
    }
  };

  const deleteDocument = async (doc: documentTableItem) => {
    try {
      const result = await axios.delete(
        `${process.env.REACT_APP_API}/deleteDocument/${doc._id}`,
        auth
      );
      return result.data;
    } catch (e) {
      console.log(`Error in deleting ${doc.documentFileName}`);
      return e;
    }
  };

  const uploadFiles = async () => {
    if (filesToUpload.length === 0) return;
    setUploadProcessing(true);
    for (let i = 0; i < filesToUpload.length; i++) {
      try {
        const docCreateResult = await axios.post(
          `${process.env.REACT_APP_API}/newdocument`,
          {
            folder: currentFolder,
            documentFileName: filesToUpload[i].name,
            documentType: 'Analysis'
          },
          auth
        );
        const tempDocUpdate = filesToUpload;
        tempDocUpdate[i].uploaded = 'Document Created';
        setFilesToUpload([...tempDocUpdate]);
        try {
          await axios.post(
            `${process.env.REACT_APP_API}/sentences/bulkadd`,
            {
              documentId: docCreateResult.data._id,
              sentences: filesToUpload[i].sentences
            },
            auth
          );
          const tempFiles = filesToUpload;
          tempFiles[i].uploaded = 'Aanlyzing Document';
          setFilesToUpload([...tempFiles]);
        } catch (e) {
          const tempFiles = filesToUpload;
          tempFiles[i].uploaded = 'Error in sentence creation';
          setFilesToUpload([...tempFiles]);
        }
      } catch (e) {
        const tempFiles = filesToUpload;
        tempFiles[i].uploaded = 'Error in doc creation';
        setFilesToUpload([...tempFiles]);
      }
    }
    setFilesToUpload([]);
    setUploadProcessing(false);
    navigate('/app/documents');
  };
  const addDocToKnowledgeBase = async (docId: string, docType: string) => {
    try {
      const result = await axios.post(
        `${process.env.REACT_APP_API}/addtoknowledgebase/${docId}`,
        { documentId: docId, knowledge_base_type: docType },
        auth
      );
      console.log('result for add to kn', result);
      return result;
    } catch (e) {
      return 'error';
    }
  };

  const kBulkUpload = async (info: FormData) => {
    const result = await axios.post(
      `${process.env.REACT_APP_API}/jsonUpload`,
      info,
      auth
    );
    return result;
  };

  const removeAllKBReferences = async () => {
    try {
      const result = await axios.put(
        `${process.env.REACT_APP_API}/removeAllKBReferences`,
        {},
        auth
      );
      console.log('result', result);
    } catch (e) {
      console.log('error', e);
    }
  };

  const value = {
    sentences,
    showModal,
    setShowModal,
    controls,
    domains,
    auth,
    getSentences,
    setSelectedControlOptions,
    setSelectedDomainOption,
    selectedControlOptions,
    selectedDomainOption,
    createPhrase,
    currentDoc,
    currentDocId,
    setCurrentDocId,
    setCurrentDoc,
    selectType,
    setSelectType,
    selectedItems,
    setSelectedItems,
    updateSentence,
    notesValue,
    setNotesValue,
    breakPhraseApart,
    updateFocus,
    filesToUpload,
    setFilesToUpload,
    uploadProcessing,
    setUploadProcessing,
    currentFolder,
    setCurrentFolder,
    uploadFiles,
    deleteDocument,
    knowledgeBaseModal,
    setKnowledgeBaseModal,
    updateRedline,
    knowledgeBaseSentences,
    selectedItemsKbResults,
    addKBsentences,
    addDocToKnowledgeBase,
    kBulkUpload,
    getCurrentSentenceKB,
    setKbLoading,
    kbLoading,
    setSelectedItemsKbResults,
    refreshCurrentSentenceKB,
    refreshKbLoading,
    removeAllKBReferences,
    updateSentenceLoading,
    getControls,
    getDomains,
    freeHandleAnalyze,
    freeformSelectedItems,
    setFreeformeSelectedItems,
    localSelectedItems,
    setLocalSelectedItems,
    kbFilterOptions,
  
  };
  return (
    <DocumentContext.Provider value={value}>
      {props.children}
    </DocumentContext.Provider>
  );
};
